import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { eventsService } from "@/api/services/events.service";
import type { CreateEventRequest } from "@/types/api.types";

export const eventKeys = {
  all: ["events"] as const,
  match: (matchId: number) => [...eventKeys.all, "match", matchId] as const,
  recent: () => [...eventKeys.all, "recent"] as const,
};

export function useMatchEvents(matchId: number) {
  return useQuery({
    queryKey: eventKeys.match(matchId),
    queryFn: () => eventsService.getMatchEvents(matchId),
    enabled: !!matchId,
    refetchInterval: 15000, // auto-refetch every 15s during live match
  });
}

export function useRecentEvents(limit = 20) {
  return useQuery({
    queryKey: eventKeys.recent(),
    queryFn: () => eventsService.getRecentEvents(limit),
    refetchInterval: 15000,
  });
}

export function useLogEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEventRequest) => eventsService.logEvent(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.match(variables.matchId),
      });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; matchId: number }) =>
      eventsService.deleteEvent(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventKeys.match(variables.matchId),
      });
    },
  });
}
